import { CONSENT, GOOGLE_ANALYTICS, X_PIXEL } from "@/lib/analytics/config";

export interface PrivacyConsent {
  ready: boolean;
  /** Optional trackers may run: GA4, PostHog, Meta and X pixels. */
  allowed: boolean;
  /**
   * The visitor explicitly accepted. Turns on PostHog session replay, and GA4 full
   * once the site is past Google's threshold (CONSENT.GA4_COOKIELESS_DEFAULT,
   * measurement-consent C11). PostHog cookies and identification need only `allowed`.
   */
  storage: boolean;
  gpc: boolean;
  country: string | null;
  source: "pending" | "gpc" | "preference" | "region" | "unknown";
  setPreference: (allowed: boolean) => void;
  refresh: () => void;
}

declare global {
  interface Window {
    privacyConsent?: PrivacyConsent;
    openPrivacyPreferences?: () => void;
  }
}

/**
 * The same decision gates SDK loading, custom events, and SPA pageviews. GPC is
 * already folded in by the bootstrap: it turns tracking off unless the visitor
 * explicitly accepted, so callers must not check `navigator.globalPrivacyControl`
 * themselves.
 */
export function isTrackingAllowed(): boolean {
  if (typeof window === "undefined") return false;
  return window.privacyConsent?.ready === true && window.privacyConsent.allowed;
}

/**
 * True only after an explicit Accept: cookies, device storage, and anything that
 * identifies a person (PostHog identify, hashed email to Google). Cookieless
 * visitors are measured but never identified.
 */
export function isStorageAllowed(): boolean {
  return isTrackingAllowed() && window.privacyConsent?.storage === true;
}

/** Runs before any tracker. No analytics requests while context is unresolved. */
export function getPrivacyBootstrapScript(): string {
  return `
(function () {
  if (window.privacyConsent) return;
  var optInCountries = ${JSON.stringify(CONSENT.EU_REGIONS)};
  var storageKey = ${JSON.stringify(CONSENT.STORAGE_KEY)};
  var gaDisableKey = ${JSON.stringify("ga-disable-" + GOOGLE_ANALYTICS.GA4_ID)};
  var ga4CookielessDefault = ${JSON.stringify(CONSENT.GA4_COOKIELESS_DEFAULT)};
  var contextReady = false;
  var contextValid = false;
  var headerGpc = false;
  var localHost = /^(localhost|127(?:\\.\\d{1,3}){3}|\\[::1\\]|0\\.0\\.0\\.0)$/.test(location.hostname);
  var inMemoryPreference = null;
  var privacy = window.privacyConsent = {
    ready: false, allowed: false, storage: false, gpc: false, country: null, source: "pending",
    setPreference: setPreference, refresh: refresh
  };
  window.blockAnalytics = true;
  window[gaDisableKey] = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  // Everything denied until the decision is made; ga-disable keeps GA4 silent
  // meanwhile. refresh() then grants GA4 storage per C11 (see GA4_COOKIELESS_DEFAULT).
  window.gtag("consent", "default", {
    analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied",
    ad_personalization: "denied", functionality_storage: "granted",
    personalization_storage: "denied", security_storage: "granted"
  });

  function readPreference() {
    try {
      var p = inMemoryPreference || JSON.parse(localStorage.getItem(storageKey) || "null");
      if (p && p.hasSetPreference === true && typeof p.analytics === "boolean" &&
          p.expires && new Date(p.expires).getTime() > Date.now()) return p;
    } catch (_) {}
    return null;
  }
  function currentGpc() { return headerGpc || navigator.globalPrivacyControl === true; }
  function savePreference(allowed) {
    // \`gpc\` records whether the choice was made while GPC was on.
    inMemoryPreference = { hasSetPreference: true, analytics: allowed, gpc: currentGpc(),
      expires: new Date(Date.now() + ${CONSENT.EXPIRATION_DAYS} * 86400000).toISOString() };
    try { localStorage.setItem(storageKey, JSON.stringify(inMemoryPreference)); } catch (_) {}
  }
  function setPreference(allowed) {
    // An explicit Accept counts everywhere, including under GPC, when it is
    // given while GPC is on (CCPA regs 7025(c)(3): a business may ask a GPC
    // visitor for consent). Decline is saved the same way.
    savePreference(allowed === true);
    refresh();
  }
  function refresh() {
    var gpc = currentGpc();
    var preference = readPreference();
    // An acceptance saved before GPC was turned on conflicts with the newer
    // signal, and GPC wins (7025(c)(3)); one given under GPC stands.
    var accepted = !!preference && preference.analytics === true && (!gpc || preference.gpc === true);
    var knownCountry = /^[A-Z]{2}$/.test(privacy.country || "") &&
      privacy.country !== "XX" && privacy.country !== "T1";
    // Precedence: explicit choice > GPC > region (opt-in list stays off).
    var allowed = !localHost && contextValid &&
      (preference ? accepted
        : !gpc && knownCountry && optInCountries.indexOf(privacy.country) === -1);
    // Explicit Accept: session replay (and GA4 full past the C11 threshold).
    var storage = allowed && accepted;
    // GA4 (C11): full for any allowed visitor while the site is under Google's
    // modeling threshold; cookieless for non-accepted visitors once past it.
    var gaFull = allowed && (accepted || !ga4CookielessDefault);
    var ready = contextReady || (gpc && !preference);
    var source = preference ? "preference" : gpc ? "gpc" : !contextReady ? "pending" : knownCountry ? "region" : "unknown";
    var changed = privacy.ready !== ready || privacy.allowed !== allowed || privacy.storage !== storage ||
      privacy.gpc !== gpc || privacy.source !== source;
    privacy.ready = ready; privacy.allowed = allowed; privacy.storage = storage; privacy.gpc = gpc; privacy.source = source;
    window.blockAnalytics = !allowed;
    window[gaDisableKey] = !allowed;
    var state = allowed ? "granted" : "denied";
    var ga = gaFull ? "granted" : "denied";
    window.gtag("consent", "update", {
      analytics_storage: ga, ad_storage: ga, ad_user_data: ga,
      ad_personalization: ga, functionality_storage: "granted",
      personalization_storage: ga, security_storage: "granted"
    });
    window.gtag("set", "allow_google_signals", gaFull);
    window.gtag("set", "allow_ad_personalization_signals", gaFull);
    window.gtag("set", "restricted_data_processing", !gaFull);
    // Revoke immediately, including an SDK whose load races the preference change.
    if (!allowed) {
      window.posthog?.opt_out_capturing?.();
      if (window.__metaPixelInitialized) window.fbq?.("consent", "revoke");
      if (window.__xPixelInitialized) window.twq?.("config", ${JSON.stringify(X_PIXEL.PIXEL_ID)}, { opt_out: true });
    }
    if (changed) window.dispatchEvent(new CustomEvent("consent_update", {
      detail: { state: state, storage: storage, source: source, gpc: gpc }
    }));
  }
  window.addEventListener("storage", function (event) {
    if (event.key === storageKey || event.key === null) { inMemoryPreference = null; refresh(); }
  });
  window.addEventListener("pageshow", refresh);
  window.addEventListener("focus", refresh);
  refresh();
  // Fetched under GPC too: a GPC visitor who accepted still needs a valid context.
  // nginx returns country + the request's Sec-GPC bit, never an IP address.
  var controller = new AbortController();
  var timeout = setTimeout(function () { controller.abort(); }, 3000);
  fetch("/privacy-context/", { cache: "no-store", credentials: "same-origin", signal: controller.signal })
    .then(function (response) { if (!response.ok) throw new Error("privacy context unavailable"); return response.json(); })
    .then(function (context) {
      if (typeof context.gpc !== "boolean" || typeof context.country !== "string") throw new Error("invalid privacy context");
      contextValid = true;
      headerGpc = context.gpc;
      privacy.country = context.country;
      contextReady = true;
      refresh();
    })
    .catch(function () { contextReady = true; refresh(); })
    .finally(function () { clearTimeout(timeout); });
})();`;
}
